import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  INotification,
  INotificationResponse,
  IMarkAsReadRequest,
  IMarkAsReadResponse,
} from '../interfaces/notification-interface';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get user notifications with pagination
   * @param loggedInUser - User email/identifier
   * @param isRecurring - Filter for recurring notifications
   * @param offset - Pagination offset (default: 0)
   * @param limit - Number of records to fetch (default: 50)
   */
  getUserNotifications(
    loggedInUser: string,
    isRecurring: boolean = false,
    offset: number = 0,
    limit: number = 50,
  ): Observable<INotificationResponse> {
    const params = new HttpParams()
      .set('LoggedInUser', loggedInUser)
      .set('IsRecurring', isRecurring.toString())
      .set('Offset', offset.toString())
      .set('Limit', limit.toString());

    return this.http.get<INotificationResponse>(
      `${this.apiUrl}/api/Notifications/GetUserNotifications`,
      { params },
    );
  }

  /**
   * Get unread notifications count
   */
  getUnreadCount(userMail: string): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${this.apiUrl}/api/Notification/GetUnreadCount/${userMail}`,
    );
  }

  /**
   * Mark single notification as read
   */
  markAsRead(notificationId: number): Observable<IMarkAsReadResponse> {
    return this.http.post<IMarkAsReadResponse>(
      `${this.apiUrl}/api/Notification/MarkAsRead`,
      { notificationId },
    );
  }

  /**
   * Mark multiple notifications as read
   */
  markMultipleAsRead(
    request: IMarkAsReadRequest,
  ): Observable<IMarkAsReadResponse> {
    return this.http.post<IMarkAsReadResponse>(
      `${this.apiUrl}/api/Notification/MarkMultipleAsRead`,
      request,
    );
  }

  /**
   * Mark all notifications as read for a user
   */
  markAllAsRead(userMail: string): Observable<IMarkAsReadResponse> {
    return this.http.post<IMarkAsReadResponse>(
      `${this.apiUrl}/api/Notification/MarkAllAsRead`,
      { userMail },
    );
  }

  /**
   * Update user notification - mark as read (single or all)
   * @param loggedInUser - User email
   * @param isReadAll - If true, mark all as read; if false, mark single notification
   * @param logId - Notification ID (required when isReadAll is false)
   * @param isRead - Read status (default: true)
   */
  updateUserNotification(
    loggedInUser: string,
    isReadAll: boolean,
    logId?: number,
    isRead: boolean = true,
  ): Observable<IMarkAsReadResponse> {
    let params = new HttpParams()
      .set('LoggedInUser', loggedInUser)
      .set('IsReadAll', isReadAll.toString());

    if (!isReadAll && logId !== undefined) {
      params = params
        .set('LogId', logId.toString())
        .set('IsRead', isRead.toString());
    }

    return this.http.post<IMarkAsReadResponse>(
      `${this.apiUrl}/api/Notifications/UpdateUserNotification`,
      null,
      { params },
    );
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: number): Observable<IMarkAsReadResponse> {
    return this.http.delete<IMarkAsReadResponse>(
      `${this.apiUrl}/api/Notification/Delete/${notificationId}`,
    );
  }

  /**
   * Get notification by ID
   */
  getNotificationById(
    notificationId: number,
  ): Observable<{ status: boolean; data: INotification; message: string }> {
    return this.http.get<{
      status: boolean;
      data: INotification;
      message: string;
    }>(`${this.apiUrl}/api/Notification/Get/${notificationId}`);
  }
}
